<script lang="ts">
import * as CdsIcons from '@central-design-system/icons';
import { computed, defineComponent, unref } from 'vue';

import { classesStates, classNames } from '@components/utils';

import {
  makeTextProps,
  makeAppearanceProps,
  makeIconProps,
  makeLinkProps,
  makeDisabledProps,
  makeReadonlyProps,
  makeLoadingProps,
  makeSizeProps,
  makeInvertColor,
  makeGroupItemProps,
  makeThemeProps,
  useAppearance,
  useLink,
  useSize,
  useLoading,
  useInvertColor,
  useIcon,
  useReadonly,
  useGroupItem,
  provideTheme
} from '../../composable';
import { CdsIcon } from '../CdsIcon';
import { CdsLoader } from '../CdsLoader';

import type { IGroupProvide } from '../../composable';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { PropType, InjectionKey } from 'vue';

export const CdsButtonToggleSymbol: InjectionKey<IGroupProvide> = Symbol.for('cds:button-toggle');

export const componentName = 'CdsButton';
export default defineComponent({
  name: componentName,
  components: { CdsLoader, CdsIcon },
  props: {
    /**
     * Кнопка активна
     */
    active: {
      type: Boolean,
      default: undefined
    },
    /**
     * Название иконки (только иконка в кнопке) или `true` для иконки по умолчанию
     */
    icon: {
      type: [String, Boolean] as PropType<TCdsIconsName | boolean>,
      default: undefined,
      validator: (value: TCdsIconsName | boolean) =>
        typeof value !== 'boolean' ? CdsIcons.allowedIcons.includes(value) : true
    },
    ...makeTextProps(),
    ...makeAppearanceProps(),
    ...makeThemeProps(),
    ...makeSizeProps(),
    ...makeIconProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLinkProps(),
    ...makeLoadingProps(),
    ...makeInvertColor(),
    ...makeGroupItemProps(),

    // Internal props
    symbol: {
      type: null,
      default: CdsButtonToggleSymbol
    }
  },
  emits: {
    'group:selected': (value: { value: boolean }) => true
  },
  setup(props, { slots, emit, attrs, expose }) {
    const { themeClasses } = provideTheme(props);
    const { appearanceClasses } = useAppearance(props, componentName);
    const { sizeClasses } = useSize(props, componentName);
    const { loadingClasses } = useLoading(props);
    const { readonlyClasses } = useReadonly(props);
    const { invertColorClasses } = useInvertColor(props, componentName);
    const { isLink, isActive: isActiveLink, tag, href, to } = useLink(props, emit);
    const { hasAppend, hasPrepend } = useIcon(props, slots, componentName);
    const groupItem = useGroupItem(props, props.symbol, false);

    const hasDefaultSlot = computed((): boolean => !!slots.default);
    const isDisabled = computed(() => unref(groupItem?.disabled) || props.disabled);
    const valueAttr = computed(() => {
      if (props.value === undefined) return undefined;

      return Object(props.value) === props.value ? JSON.stringify(props.value, null, 0) : props.value;
    });

    const isActive = computed(() => {
      if (props.active !== undefined) {
        return props.active;
      }

      if (unref(isLink)) {
        return unref(isActiveLink);
      }

      return unref(groupItem?.isSelected);
    });

    const iconClasses = computed(() => ({
      'cds-button__icon': !!props.icon
    }));

    const internalIcon = computed(() => {
      return typeof props.icon !== 'boolean' ? props.icon : undefined;
    });

    const classes = computed(() =>
      classNames(
        {
          ['cds-button--active']: unref(isActive),
          [classesStates.disabled]: unref(isDisabled)
        },
        unref(themeClasses),
        unref(appearanceClasses),
        unref(sizeClasses),
        unref(loadingClasses),
        unref(readonlyClasses),
        unref(invertColorClasses),
        unref(iconClasses),
        unref(groupItem?.selectedClass)
      )
    );

    function onClick(event: MouseEvent) {
      if (
        unref(isDisabled) ||
        (unref(isLink) &&
          (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0 || attrs.target === '_blank'))
      )
        return;

      groupItem?.toggle();
    }

    expose({
      groupItem,
      toggle: () => groupItem?.toggle(),
      isDisabled
    });

    return {
      href,
      to,
      tag: unref(isLink) ? tag : 'button',
      type: !unref(isLink) ? 'button' : undefined,
      valueAttr,
      internalIcon,
      hasPrepend,
      hasAppend,
      hasDefaultSlot,
      isDisabled,
      classes,
      onClick
    };
  }
});
</script>

<template>
  <component
    :is="tag"
    :type="type"
    :to="to"
    :href="href"
    :disabled="isDisabled || loading"
    :value="valueAttr"
    :class="classes"
    class="cds-button"
    @click="onClick"
  >
    <span v-if="hasPrepend || !!icon" class="cds-button__prepend">
      <!--
        @slot Слот для иконки в начале
          @type CdsIcon | svg
      -->
      <cds-icon v-if="prependIcon || !!internalIcon" :name="prependIcon || internalIcon" />
      <slot name="prepend" />
    </span>
    <span v-if="hasDefaultSlot || !!text" class="cds-button__text" data-no-activator="">
      <template v-if="!!text">
        {{ text }}
      </template>
      <!--
        @slot Слот для текста
          @type string
      -->
      <slot v-else />
    </span>
    <span v-if="hasAppend" class="cds-button__append">
      <!--
        @slot Слот для иконки в конце
        @type CdsIcon | svg
      -->
      <cds-icon v-if="appendIcon" :name="appendIcon" />
      <slot name="append" />
    </span>
    <cds-loader v-if="loading" size="xs" fixed />
  </component>
</template>

<style lang="scss">
@import './CdsButton';
</style>
